import { Flex } from "antd";
import { HoverableCard } from "components/HoverableCard";
import { forwardRef, useMemo } from "react";
import { Header } from "./Preview/components/Header";
import { Logo } from "./Preview/components/Logo";
import { Presenter } from "./Preview/components/Presenter";
import { TemplateImages } from "./Preview/components/TemplateImages";
import { TemplateVideos } from "./Preview/components/TemplateVideos";
import { TemplateCustomTexts } from "./Preview/components/TemplateCustomTexts";
import { ColoredBackground } from "./Preview/components/ColoredBackground";
import { TemplateShapes } from "./Preview/components/TemplateShapes";
import { TemplateTable } from "./Preview/components/TemplateTable";
import { LAYOUT_PREVIEW_OUTLINE_WIDTH } from "../../constants";
import { Slide } from "pages/media/types/";
import { TemplateParameteredTexts } from "./Preview/components/TemplateParameteredTexts";
import { StyledPreview } from "./SlideEditableTemplate";
import { ImageVideoPreview } from "../common/ImageVideoPreview";
import { ITEM_TYPES, Size, TemplateState } from "../../types";
import { TemplateMainImage } from "./Preview/components/TemplateMainImage";
import { radius } from "@libertify/ui";

export type SlideTemplatePreviewProps = {
  template: TemplateState;
  onCardClick: () => void;
  border: {
    borderWidth?: number;
    borderColor?: string;
    borderRadius?: number;
  };
  opacity?: number;
  templateSize: Size;
  slide: Slide;
  marginBlockEnd?: number;
};

export const SlideTemplatePreview = forwardRef<
  HTMLDivElement,
  SlideTemplatePreviewProps
>(
  (
    {
      template,
      onCardClick,
      border: {
        borderWidth = LAYOUT_PREVIEW_OUTLINE_WIDTH,
        borderColor,
        borderRadius = radius.box,
      },
      opacity = 0.3,
      templateSize,
      slide,
      marginBlockEnd = 18,
    },
    ref
  ) => {
    const { width, height } = templateSize;

    const backgroundMediaItem = useMemo(() => {
      if (template.background.bodyView === "Video") {
        return {
          type: ITEM_TYPES.Video,
          assetId: template.background.bodyVideoAsset,
          assetPath: template.background.bodyVideo,
        };
      } else if (template.background.bodyView === "Picture") {
        return {
          type: ITEM_TYPES.Image,
          assetId: template.background.bodyImageAsset,
          assetPath: template.background.bodyImage,
        };
      } else {
        return undefined;
      }
    }, [template]);

    return (
      <Flex
        vertical
        style={{ position: "relative", paddingBlock: 3, marginBlockEnd }}
      >
        <HoverableCard
          ref={ref}
          data-interactive
          data-testid="hoverable-card"
          hoverable
          styles={{
            body: {
              padding: 0,
              overflow: "hidden",
              // Clamp at 0: a negative radius is invalid CSS and gets ignored,
              // which lets antd Card's default (rounded bottom corners) show.
              borderRadius: Math.max(0, borderRadius - borderWidth),
              width: "fit-content",
            },
          }}
          opacity={opacity}
          style={{
            borderRadius,
            borderWidth,
            borderColor,
            width: "fit-content",
            margin: "auto",
          }}
          onClick={onCardClick}
        >
          <StyledPreview width={width} height={height}>
            {backgroundMediaItem?.assetId && (
              <ImageVideoPreview
                type={backgroundMediaItem.type}
                assetId={backgroundMediaItem.assetId}
                path={backgroundMediaItem.assetPath}
              />
            )}
            {template.background.bodyColor && (
              <ColoredBackground
                width={width}
                height={height}
                backgroundColor={template.background.bodyColor}
                disableOnclick
              />
            )}
            <TemplateImages
              templateImages={template.images}
              previewWidth={width}
              previewHeight={height}
            />
            <TemplateMainImage
              previewWidth={width}
              previewHeight={height}
              slide={slide}
              mainImage={template.mainImage}
            />
            <TemplateVideos
              templateVideos={template.videos}
              previewWidth={width}
              previewHeight={height}
            />
            <TemplateCustomTexts
              previewWidth={width}
              previewHeight={height}
              customTexts={template.customTexts}
            />
            <TemplateParameteredTexts
              previewWidth={width}
              previewHeight={height}
              texts={template.parameteredTexts ?? []}
              figure={template.figure}
            />
            <TemplateShapes
              shapes={[
                ...template.shapes,
                ...(template.slideLayoutShapes ?? []),
              ]}
              previewWidth={width}
              previewHeight={height}
            />
            {template.table && "bullet_points" in slide.placeholders && (
              <TemplateTable
                table={template.table}
                bulletPoints={slide.placeholders.bullet_points}
                previewWidth={width}
                previewHeight={height}
              />
            )}
            {template.logo.showLogo && template.logo.assetId && (
              <>
                <Header
                  headerItem={template.header}
                  previewWidth={width}
                  previewHeight={height}
                />
                <div
                  data-testid="logo"
                  style={{
                    position: "absolute",
                    zIndex: template.logo.layerIndex,
                    top: template.logo.position.y * height,
                    left: template.logo.position.x * width,
                    width: template.logo.size.width * width,
                    height: template.logo.size.height * height,
                    transformOrigin: "center",
                    transform: template.logo.rotation
                      ? `rotate(${template.logo.rotation}deg)`
                      : undefined,
                  }}
                >
                  <Logo
                    logo={template.logo}
                    previewWidth={width}
                    previewHeight={height}
                  />
                </div>
              </>
            )}
            {template.presenter.presenter && (
              <Presenter
                presenter={template.presenter}
                previewWidth={width}
                previewHeight={height}
              />
            )}
          </StyledPreview>
        </HoverableCard>
      </Flex>
    );
  }
);

SlideTemplatePreview.displayName = "SlideTemplatePreview";
